import { EventEmitter } from 'eventemitter3';
import { AppAudioDecoder } from './AppAudioDecoder';
import { AudioEncoder } from './AudioEncoder';
import { AudioFrameExtractor } from './AudioFrameExtractor';
import { createAudioFrameExtractor } from './createAudioFrameExtractor';
import { IncomingTrack } from './IncomingTrack';
import { AudioPlayer } from './multiaudioplayer/AudioPlayer';
export class AudioEngine {
    LOG_PREFIX = '[AudioEngine]';
    audioFrameExtractors = new Map();
    audioEncoders = new Map();
    audioDecoders = new Map();
    audioContext;
    audioPlayer;
    incomingTracks = new Map();
    encodedAudioOutputTrackAddedCallback;
    referenceTimeEmitter = new EventEmitter();
    constructor(audioContext) {
        this.audioContext = audioContext;
        //this.initializeWorker();
    }
    async init(getOptimalDelayCallback, encodedAudioOutputTrackAddedCallback) {
        this.encodedAudioOutputTrackAddedCallback =
            encodedAudioOutputTrackAddedCallback;
        this.audioPlayer = new AudioPlayer(this.audioContext, getOptimalDelayCallback);
        this.audioPlayer.on('newReferenceTimes', (trackId, presentationTimeMicros, realTimeMillis) => {
            this.referenceTimeEmitter.emit('newReferenceTimes', trackId, presentationTimeMicros, realTimeMillis);
        });
        await this.audioPlayer.init();
    }
    async destroy() {
        await this.audioPlayer?.dispose();
    }
    async addMediaStreamAudioTrack(track) {
        const trackId = track.id;
        // Create and initialize frame extractor
        const audioFrameExtractor = createAudioFrameExtractor(track);
        if (audioFrameExtractor instanceof AudioFrameExtractor) {
            await AudioFrameExtractor.initialize(this.audioContext);
        }
        this.audioFrameExtractors.set(trackId, audioFrameExtractor);
        // Create and start audio encoder
        const audioEncoder = new AudioEncoder(trackId);
        this.audioEncoders.set(trackId, audioEncoder);
        await audioEncoder.start(audioFrameExtractor.getOutputTrack());
        // Get the encoded output track and notify callback
        const encodedOutputTrack = audioEncoder.getEncodedOutputTrack();
        this.encodedAudioOutputTrackAddedCallback?.(encodedOutputTrack);
        // Start extracting frames
        audioFrameExtractor.start();
    }
    async removeMediaStreamAudioTrack(track) {
        const trackId = track.id;
        // Stop extractor
        const audioFrameExtractor = this.audioFrameExtractors.get(trackId);
        if (audioFrameExtractor) {
            await audioFrameExtractor.stop();
            this.audioFrameExtractors.delete(trackId);
        }
        // Encoder will automatically shutdown when it receives done from extractor
        this.audioEncoders.delete(trackId);
    }
    async feedEncodedAudioFrame(frame) {
        // Get or create decoder for this track
        let decoder = this.audioDecoders.get(frame.trackId);
        if (!decoder) {
            decoder = new AppAudioDecoder(frame.trackId);
            await decoder.init((error) => {
                console.error(`${this.LOG_PREFIX} decoder error for track ${frame.trackId}:`, error);
            });
            // Set up listener on decoder output to play audio
            decoder
                .getUnencodedOutputTrack()
                .setListener(async (unencodedAudioFrame, done) => {
                if (done) {
                    this.audioDecoders.delete(frame.trackId);
                    return;
                }
                if (unencodedAudioFrame) {
                    this.audioPlayer?.feedAudioPacket({
                        trackId: unencodedAudioFrame.trackId,
                        audioData: {
                            channels: [unencodedAudioFrame.data],
                            timestamp: unencodedAudioFrame.timestamp,
                            duration: unencodedAudioFrame.duration,
                        },
                        endOfTrack: unencodedAudioFrame.done,
                    });
                }
            });
            this.audioDecoders.set(frame.trackId, decoder);
        }
        // Also track for handleLeave cleanup
        if (!this.incomingTracks.has(frame.trackId)) {
            this.incomingTracks.set(frame.trackId, new IncomingTrack(frame.trackId, frame.userSessionId, async (f) => {
                if (f) {
                    decoder?.feedEncodedAudioFrame(f);
                }
            }));
        }
        this.incomingTracks.get(frame.trackId)?.feedFrame(frame, frame.done);
    }
    async handleLeave(userSessionId) {
        const trackIdsToDelete = [];
        for (const [trackId, track] of this.incomingTracks) {
            if (track.getUserSessionId() === userSessionId) {
                trackIdsToDelete.push(trackId);
            }
        }
        for (const trackId of trackIdsToDelete) {
            this.incomingTracks.delete(trackId);
            // Clean up decoder for this track
            const decoder = this.audioDecoders.get(trackId);
            if (decoder) {
                await decoder.destroy();
                this.audioDecoders.delete(trackId);
            }
        }
    }
}
